import React from "react";
import TableRow from "@mui/material/TableRow";
import MuiTableCell from "@mui/material/TableCell";
import IconButton from "@mui/material/IconButton";
import Edit from "@mui/icons-material/Edit";
import Visibility from "@mui/icons-material/Visibility";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import Archive from "@mui/icons-material/Archive";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Assignment from "@mui/icons-material/Assignment";
import ListAlt from "@mui/icons-material/ListAlt";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import { TABLE_ACTIONS } from "helpers/constants";
import { Checkbox, styled, Tooltip } from "@mui/material";
import { ActionItem } from "./types";

const TableCell = styled(MuiTableCell)(({ theme }) => ({
  height: "40px",
}));

export type Props = {
  data: string[];
  row: any;
  selected: boolean;
  handleSelected: any;
  handleActionClicked: any;
  isSelectable: boolean;
  allowedMenuItems: string[] | ((data: any) => string[]);
  extraActionItems: ActionItem[];
  displayActionInMenu?: boolean;
};

export default function CustomTableRow({
  data,
  row,
  selected,
  handleSelected,
  handleActionClicked,
  isSelectable,
  allowedMenuItems,
  extraActionItems,
  displayActionInMenu = true,
}: Props) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const defaultActionItems: ActionItem[] = [
    {
      name: TABLE_ACTIONS.VIEW,
      icon: <Visibility />,
      color: "success",
    },
    {
      name: TABLE_ACTIONS.EDIT,
      icon: <Edit />,
    },
    {
      name: TABLE_ACTIONS.DELETE,
      icon: <DeleteOutline color="error" />,
      color: "error",
    },
    {
      name: TABLE_ACTIONS.ARCHIVE,
      icon: <Archive />,
      color: "error",
    },
    {
      name: TABLE_ACTIONS.VIEW_ANSWERS,
      icon: <ListAlt />,
      color: "info",
    },
    {
      name: TABLE_ACTIONS.ANSWER_SURVEY,
      icon: <Assignment />,
      color: "primary",
    },
  ];

  const isMenuAllowed = (menuitem: string) =>
    typeof allowedMenuItems === "function"
      ? allowedMenuItems(row).includes(menuitem)
      : allowedMenuItems.includes(menuitem);

  return (
    <>
      <TableRow hover>
        <TableCell padding="checkbox">
          {isSelectable && (
            <Checkbox
              disableRipple
              checked={selected}
              onChange={handleSelected}
            />
          )}
        </TableCell>
        {data.map((entry, index) => {
          if (entry !== "actions") {
            return (
              <TableCell sx={{ padding: 1 }} key={`r_${entry}_${index}`}>
                {entry}
              </TableCell>
            );
          } else {
            return (
              <TableCell
                sx={{ padding: 1 }}
                key={`r_${entry}_${index}`}
                align="right"
              >
                {!displayActionInMenu &&
                  [...extraActionItems, ...defaultActionItems].map((item) => {
                    return (
                      isMenuAllowed(item.name) && (
                        <Tooltip title={item.name} key={item.name}>
                          <IconButton
                            aria-label={item.name}
                            key={item.name}
                            onClick={() => {
                              handleActionClicked(item.name);
                            }}
                            color={item.color || "primary"}
                          >
                            {item.icon}
                          </IconButton>
                        </Tooltip>
                      )
                    );
                  })}
                {displayActionInMenu && (
                  <>
                    <Tooltip title={"Actions"}>
                      <IconButton
                        id="basic-button"
                        aria-controls={open ? "basic-menu" : undefined}
                        aria-haspopup="true"
                        aria-expanded={open ? "true" : undefined}
                        onClick={handleClick}
                      >
                        <MoreVertIcon color={"primary"} />
                      </IconButton>
                    </Tooltip>
                    <Menu
                      id="basic-menu"
                      anchorEl={anchorEl}
                      open={open}
                      onClose={handleClose}
                      MenuListProps={{
                        "aria-labelledby": "basic-button",
                      }}
                    >
                      {[...extraActionItems, ...defaultActionItems].map(
                        (item) => {
                          return (
                            isMenuAllowed(item.name) && (
                              <MenuItem
                                key={item.name}
                                onClick={() => {
                                  handleActionClicked(item.name);
                                  handleClose();
                                }}
                                color={item.color || "primary"}
                              >
                                <ListItemIcon>{item.icon}</ListItemIcon>
                                <ListItemText
                                  sx={{
                                    color: item.color ? item.color : "default",
                                  }}
                                >
                                  {item.name}
                                </ListItemText>
                              </MenuItem>
                            )
                          );
                        },
                      )}
                    </Menu>
                  </>
                )}
              </TableCell>
            );
          }
        })}
      </TableRow>
    </>
  );
}
